import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../services/apiService';
import { Administrador } from '../../data/Administrador';
import { ListaPaginada } from '../../data/ListaPaginada';
import Pagination from '../../componets/Pagination';
import ConfirmationModal from '../../componets/ConfirmationModal';

function Administradores() {
  const [data, setData] = useState<ListaPaginada<Administrador> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<number | null>(null);
  const itemsPerPage = 10; // Itens por página

  useEffect(() => {
    const fetchAdministradores = async () => {
      setLoading(true);
      try {
        const endpoint = `/usuarios/administradores?pagina=${currentPage}&tamanhoPagina=${itemsPerPage}`;
        const responseData = await apiRequest<ListaPaginada<Administrador>>(endpoint, 'GET', true);
        setData(responseData);
      } catch (err) {
        setError((err as Error).message || 'Erro ao carregar os administradores.');
      } finally {
        setLoading(false);
      }
    };
    fetchAdministradores();
  }, [currentPage]);

  const totalRecords = data?.totalItens ?? 0;
  const totalPages = data?.totalPaginas ?? 0;
  const currentItems = data?.itens ?? [];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Abre o modal de confirmação
  const handleDeleteClick = (id: number) => {
    setAdminToDelete(id);
    setShowDeleteModal(true);
  };

  // Executa a exclusão após a confirmação
  const confirmDelete = async () => {
    if (!adminToDelete) return;

    setShowDeleteModal(false);
    setLoading(true);
    setError(null);

    try {
      // Assumindo um endpoint POST para exclusão segura
      await apiRequest(`usuarios/deleteadministrador/${adminToDelete}`, 'POST', true);

      // Recarrega os dados da página atual para refletir a exclusão
      const fetchAdministradores = async () => {
        const endpoint = `/usuarios/administradores?pagina=${currentPage}&tamanhoPagina=${itemsPerPage}`;
        const responseData = await apiRequest<ListaPaginada<Administrador>>(endpoint, 'GET', true);
        setData(responseData);
      };
      await fetchAdministradores();
    } catch (err) {
      setError((err as Error).message || 'Erro ao excluir o administrador.');
    } finally {
      setLoading(false);
      setAdminToDelete(null);
    }
  };

  if (loading) {
    return <p>Carregando Administradores...</p>;
  }

  if (error) {
    return <p>Erro: {error}</p>;
  }

  return (
    <div className="row">
      <div className="card">
        <div className="card-body">
          <div className="mb-3">
            <h1 className="h3 d-inline align-middle">Administradores</h1>
          </div>
          <div className="mb-3 text-end">
            <Link to="/administradores/novo" className="btn btn-primary"> Novo <i className="bi bi-plus"></i></Link>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th className="col-2">#</th>
                <th className="col">Nome</th>
                <th className="col">Creci</th>
                <th className="col-3 text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
            {currentItems.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center">Nenhum administrador cadastrado.</td>
              </tr>
            ) : (currentItems.map(administrador => (
              <tr key={administrador.id}>
                <td>{administrador.id}</td>
                <td>{administrador.nome}</td>
                <td>{administrador.creci}</td>
                <td>
                  <div className="d-flex justify-content-center gap-2">
                    <Link to={`/administradores/editar/${administrador.id}`} className="btn btn-outline-primary"><i className="align-middle bi bi-pencil-square"></i> Editar</Link>
                    <button onClick={() => handleDeleteClick(administrador.id!)} className="btn btn-outline-danger">
                      <i className="align-middle bi bi-trash"></i> Excluir
                    </button>
                  </div>
                </td>
              </tr>
              ))
            )}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecords={totalRecords}
            onPageChange={handlePageChange}
          />
          <ConfirmationModal
            show={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            title="Confirmar Exclusão"
            body="Tem certeza que deseja excluir este administrador? Esta ação não pode ser desfeita."
            confirmText="Excluir"
            confirmButtonVariant="danger"
          />
        </div>
      </div>
    </div>
  );
}

export default Administradores;